import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SharedTable } from '@hero/web-components';
import { OrderTableFilters } from './OrderTable';
import { DateType, dateTypeSchema } from '@hero/schema';

export interface OrderTableHeadProps {
  filters?: OrderTableFilters;
}

/**
 * OrderTableHead component that renders search and filters
 * Used internally by OrderTable when filters prop is provided.
 * Memoized so it only re-renders when filters change, not when table data (loads) updates.
 */
export const OrderTableHead = React.memo<OrderTableHeadProps>(function OrderTableHead({ filters }) {
  const { t } = useTranslation('c-dates');

  if (!filters) return null;

  const { showSearch, statusFilter, dateFilter } = filters;

  // All DateType options from schema; labels from c-dates (filters.date_types)
  const defaultDateOptions = useMemo(
    () => [
      { value: 'today' as DateType, label: t('relative_dates.today') },
      { value: 'yesterday' as DateType, label: t('relative_dates.yesterday') },
      { value: 'this_week' as DateType, label: t('relative_dates.this_week') },
      { value: 'last_week' as DateType, label: t('relative_dates.last_week') },
      { value: 'this_month' as DateType, label: t('relative_dates.this_month') },
      { value: 'last_month' as DateType, label: t('relative_dates.last_month') },
    ],
    [t],
  );

  const dateOptions = dateFilter?.options || defaultDateOptions;

  return (
    <SharedTable.Head>
      {showSearch && <SharedTable.Search />}

      {(statusFilter || dateFilter) && (
        <div className="flex items-center gap-3 flex-wrap">
          {statusFilter && (
            <SharedTable.Badge
              options={statusFilter.options}
              counts={statusFilter.counts}
              value={statusFilter.value}
              onChange={statusFilter.onChange as (value: string | undefined) => void}
              mode="inline"
              showCounts={!!statusFilter.counts}
            />
          )}

          {dateFilter && (
            <SharedTable.Badge
              options={dateOptions as Array<{ value: string; label: string }>}
              value={dateFilter.value}
              onChange={dateFilter.onChange as (value: string | undefined) => void}
              mode="dropdown"
              placeholder={dateFilter.placeholder || t('order_dates.pickup_date')}
              showCounts={false}
            />
          )}
        </div>
      )}
    </SharedTable.Head>
  );
});
